import type {
  IMapLayout,
  IMapLayoutFinish,
  IMapLayoutRoomEntry,
} from '@interfaces/map';

import {
  findCatalogElement,
  type MapEditorCatalogElement,
} from './mapEditorCatalog';
import { parseLayoutFinishes } from './mapEditorFinishes';
import { EMPTY_FINISH_SET, type MapLayoutFinishSet } from './mapEditorUnreal';

// A room in the draft: which catalog element, and which placement of it this is.
//
// The key is the whole reason this is not a bare id. A spot's identity is authored into its
// module's level asset, so placing the same module twice brings the same spot ids twice and any
// content assigned to one shows up in both. UE builds a per-placement spot id from this key, which
// makes those two copies addressable apart. The composite is UE's to format and opaque here: it is
// read out of CURRENT_GEOMETRY_LIST and passed through, never constructed or parsed.
export interface MapEditorRoom {
  id: string;
  key: string;
}

// Counts keys minted in this browser session, so two rooms added a millisecond apart cannot
// collide however coarse the clock is.
let mintedCount = 0;

/**
 * Mints a key for a placement the author has just created.
 *
 * Random rather than derived: a key derived from the room's position reintroduces exactly the
 * problem it exists to remove (inserting a room ahead of it would move it), and one derived from
 * the element id collides on the duplicate-module case, which is the case per-placement content
 * exists for.
 *
 * `r` prefixed so a minted key stays out of the `i<index>` namespace UE stamps itself — see
 * legacyRoomKey, which is the one place the hub deliberately writes into it. Math.random rather than
 * crypto.randomUUID: this also runs under Jest's jsdom, which is older than WebCrypto, and the key
 * only has to be unique within one layout.
 *
 * @returns An opaque token, unique per call.
 */
export const mintRoomKey = (): string => {
  mintedCount += 1;

  const stamp = Date.now().toString(36);
  const noise = Math.random().toString(36).slice(2, 6);

  return `r${stamp}${mintedCount.toString(36)}${noise}`;
};

/**
 * The key a room out of a v1 record stands under: its position in the republished chain.
 *
 * Index-derived, which the rules above otherwise forbid, and this is the one case where it is the
 * only correct answer. A v1 record holds no placement identity at all, so the only identity its rooms
 * have is the one UE already gave them: it stamps `i<index>` (MakeFallbackRoomKey) on every room that
 * arrives without a key, which is every room the hub sent before this change. Reproducing that exact
 * string is what keeps the republish on entry a no-op — UE's `AreLayoutRoomsEquivalent` counts
 * `RoomKey`, so an unchanged chain under unchanged keys is answered by doing nothing, and every spot
 * id already standing in the world keeps addressing the spot it addressed yesterday.
 *
 * Minting here instead is the trap, and it is not a small one: a random key differs per client and
 * per session, so every arrival would publish a chain UE cannot recognise and the world would be torn
 * down and rebuilt — re-landing everyone standing in it — on every entry, for as long as the record
 * stays v1.
 *
 * The index stops being an index the moment it is written down. The first Save persists these keys
 * explicitly, and from then on they travel with their room like any minted one, so inserting a room
 * ahead of them no longer moves them.
 *
 * @param index - Position in the chain this hub will republish, which is the position UE counts too.
 * @returns The string UE's own fallback produces for that position.
 */
export const legacyRoomKey = (index: number): string => `i${index}`;

/**
 * The element ids of a draft, in order.
 *
 * @param rooms - Rooms to read.
 * @returns Just the ids, for the places that describe geometry rather than placement.
 */
export const roomIdsOf = (rooms: MapEditorRoom[]): string[] =>
  rooms.map((room) => room.id);

/**
 * Drops a cap a saved layout carries as one of its own rooms.
 *
 * Such a row is drift: caps are not offered in the picker (`getPickableElements`) and are appended at
 * publish time instead, so only a record written before that rule can hold one. Left in the draft it
 * can never match the geometry report — UE reports a cap as a cap, and the report parser drops every
 * one of them, because the cap this hub appends itself arrives on the wire as `role: 'end'` with
 * `auto: false`, indistinguishable from a saved one. The draft would then stand one row longer than
 * what is reported standing and read as unsaved work forever: not once, but on every entry, since the
 * record is re-read each session.
 *
 * Dropped only when the publish will put the very same element back (`appendEndCap` appends
 * `defaultEndElement` to a chain that does not already end in a cap), so the world keeps the wall it
 * has. A map whose kit no longer names a default cap keeps the saved row untouched — a visible wall
 * the author cannot explain beats a silently demolished one.
 *
 * Either way the row is this hub's to account for rather than the author's edit, so a record that
 * carried one is hydrated as *published* — see the persistence bridge. Without that, the drop shows
 * up as their unsaved change, and the row this leaves in place shows up as one on every entry for as
 * long as the record stands: the geometry report drops every cap, so a draft holding one can never
 * match it.
 *
 * @param rooms - Rooms as saved.
 * @param capId - The catalog's `defaultEndElement`; empty leaves the list alone.
 * @returns The rooms with any trailing run of that element removed.
 */
export const dropTrailingCap = (
  rooms: MapEditorRoom[],
  capId: string,
): MapEditorRoom[] => {
  if (!capId) {
    return rooms;
  }

  let end = rooms.length;
  while (end > 0 && rooms[end - 1].id === capId) {
    end -= 1;
  }

  return end === rooms.length ? rooms : rooms.slice(0, end);
};

/**
 * Drops a lobby a saved layout carries as one of its own rooms.
 *
 * The mirror of dropTrailingCap, and it is drift of exactly the same kind: the picker never offers a
 * Start element (`getPickableElements`) and the lobby is prepended at publish time instead, so only
 * a record written before that rule can hold one as a room. The flat catalog list that preceded the
 * picker had no notion of a role and listed every element, which is where such a record comes from.
 *
 * Left in the draft the row can never match the geometry report — the report parser drops every
 * anchor row, because the lobby this hub prepends itself comes back as `role: 'start'` with
 * `auto: false`, indistinguishable from a saved one. The draft would then stand one row longer than
 * what is reported standing and read as unsaved work on every entry, for as long as the record
 * stands. It also silently disables the lobby control: `prependStartRoom` declines to prepend to a
 * chain that already opens on a lobby, so a newly chosen entrance would be persisted and never sent.
 *
 * Matched by role rather than by id, unlike the cap: the row names whichever lobby that record was
 * written with, which need not be the one the kit now calls its default. A leading run is dropped
 * rather than a single row, for the same reason the cap drops one — a record holding two is drift of
 * the same kind, and leaving the second would leave the chain still opening on a lobby.
 *
 * The dropped id is the record's own answer to which entrance it opens in, so a caller that has no
 * `startElement` should adopt it rather than discard it — see the persistence bridge.
 *
 * @param rooms - Rooms as saved.
 * @param elements - The resolved catalog. An id it cannot describe is not a lobby.
 * @returns The rooms with any leading run of Start elements removed.
 */
export const dropLeadingLobby = (
  rooms: MapEditorRoom[],
  elements: MapEditorCatalogElement[],
): MapEditorRoom[] => {
  let start = 0;
  while (
    start < rooms.length &&
    findCatalogElement(elements, rooms[start].id)?.role === 'start'
  ) {
    start += 1;
  }

  return start === 0 ? rooms : rooms.slice(start);
};

/**
 * Whether two room lists describe the same layout: the same elements, in the same order, standing
 * under the same placement keys.
 *
 * The keys are part of it because they are part of it for UE too — `AreLayoutRoomsEquivalent`
 * compares `RoomKey` alongside the element, so a chain differing only in keys is *not* already
 * standing: it rebuilds, and re-stamps every spot id in it. Two consequences of getting this wrong,
 * both real: reordering two copies of one module changes nothing about the geometry and everything
 * about which placement holds which content, and comparing ids alone left that edit unpublishable
 * because Save is gated on dirtiness; and a draft whose keys disagree with the world's — a layout
 * built by an older sender, say — read as saved while none of its spots were addressable.
 *
 * @param a - One list.
 * @param b - The other.
 * @returns True when the same placements stand in the same order.
 */
export const sameRoomPlacements = (
  a: MapEditorRoom[],
  b: MapEditorRoom[],
): boolean =>
  a.length === b.length &&
  a.every(
    (room, index) => room.id === b[index].id && room.key === b[index].key,
  );

/**
 * Reads `layout.rooms` off a branding record, in either version.
 *
 * A v1 entry is a bare id with no key and takes the one UE already stamped on it — see legacyRoomKey
 * for why that is derived from its position rather than minted. The record itself is left alone until
 * its author next saves, since a layout nobody edits does not need rewriting.
 *
 * Deterministic end to end, which matters more than it looks: the bridge hydrates the draft *and*
 * republishes the layout from one call, and every participant entering the room runs the same call
 * against the same record. Two clients therefore describe the same placements, and so does the same
 * client tomorrow.
 *
 * Defensive per entry, because this crosses a DB record that a newer or buggier writer may have
 * touched: a malformed entry costs its own row rather than the whole layout, and a key that repeats
 * is re-minted, since two placements sharing a key would address one another's spots.
 *
 * @param rooms - The record's `rooms` array, or whatever was found in its place.
 * @returns The rooms in saved order, every one carrying a key.
 */
export const readPersistedRooms = (
  rooms: IMapLayout['rooms'] | null | undefined,
): MapEditorRoom[] => {
  if (!Array.isArray(rooms)) {
    return [];
  }

  const seenKeys = new Set<string>();

  return rooms.reduce<MapEditorRoom[]>((parsed, entry) => {
    const room =
      typeof entry === 'string'
        ? { id: entry, key: '' }
        : (entry as Partial<IMapLayoutRoomEntry> | null);

    if (typeof room?.id !== 'string' || !room.id) {
      return parsed;
    }

    // Counted over the rooms that survive rather than over the record's entries, because that is what
    // UE counts: its fallback numbers the payload it receives, and a dropped entry is never sent.
    const savedKey =
      typeof room.key === 'string' && room.key.trim()
        ? room.key.trim()
        : legacyRoomKey(parsed.length);

    // A repeat can only come from a hand-edited record or a bug, and it is re-minted rather than
    // trusted: two placements under one key address each other's spots. Minting costs this room its
    // existing spot ids once, which is the cheaper of the two failures.
    const key = seenKeys.has(savedKey) ? mintRoomKey() : savedKey;
    seenKeys.add(key);

    return [...parsed, { id: room.id, key }];
  }, []);
};

/**
 * What Save writes to the branding record.
 *
 * Nothing is minted here. A Save that re-mints keys is a Save that re-points every piece of content
 * assigned to the layout, so a room reaches this function with the key it was given when the author
 * added it — or, for a room restored from a v1 record, when that record was read.
 *
 * @param rooms - The draft being saved.
 * @returns The v2 `rooms` array.
 */
export const toPersistedRooms = (
  rooms: MapEditorRoom[],
): IMapLayoutRoomEntry[] =>
  rooms.map((room) => ({ id: room.id, key: room.key }));

/**
 * Reads the finishes off a branding record.
 *
 * Overrides are dropped where the placement they name did not survive the room read — a key that was
 * re-minted for repeating, or an entry that was malformed. Keeping them would leave the layout
 * carrying surfaces for rooms that are not in it, which publishes bytes nobody can see and grows
 * every time the record is rewritten.
 *
 * @param layout - The record's `layout`, or whatever was found in its place.
 * @param rooms - The rooms already read out of the same record, which decide which keys are real.
 * @returns The defaults and the surviving overrides.
 */
export const readPersistedFinishes = (
  layout: IMapLayout | null | undefined,
  rooms: MapEditorRoom[],
): MapLayoutFinishSet => {
  const defaults = parseLayoutFinishes(layout?.finishDefaults) ?? [];
  const raw = layout?.roomFinishes;
  if (!raw || typeof raw !== 'object') {
    return defaults.length ? { defaults, rooms: {} } : EMPTY_FINISH_SET;
  }

  const live = new Set(rooms.map((room) => room.key).filter(Boolean));

  const overrides = Object.entries(raw).reduce<
    Record<string, IMapLayoutFinish[]>
  >((parsed, [key, entries]) => {
    if (!live.has(key)) {
      return parsed;
    }

    const finishes = parseLayoutFinishes(entries) ?? [];

    return finishes.length ? { ...parsed, [key]: finishes } : parsed;
  }, {});

  return { defaults, rooms: overrides };
};

/**
 * What Save writes for the finishes.
 *
 * Empty halves are omitted rather than written as empty containers, so a layout nobody has finished
 * is byte-identical to what this hub wrote before finishes existed — and a reader older than the
 * feature sees exactly the record it expects.
 *
 * @param finishes - The draft's finishes.
 * @returns The fields to merge into the saved layout.
 */
export const toPersistedFinishes = (
  finishes: MapLayoutFinishSet,
): Pick<IMapLayout, 'finishDefaults' | 'roomFinishes'> => ({
  ...(finishes.defaults.length ? { finishDefaults: finishes.defaults } : {}),
  ...(Object.keys(finishes.rooms).length
    ? { roomFinishes: finishes.rooms }
    : {}),
});
